import { useAuth } from './auth.js';

const BASE = import.meta.env.VITE_API_BASE ?? '';

export interface ApiErrorBody {
  code: string;
  message: string;
  field?: string | null;
  requestId?: string;
  issues?: { field: string; message: string }[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiError';
  }
  get code(): string { return this.body.code; }
  get field(): string | null { return this.body.field ?? null; }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** ไม่แนบ token — ใช้กับ endpoint ก่อน login */
  anonymous?: boolean;
  /** ไม่แนบ X-Tenant-Id — ใช้กับ endpoint ระดับระบบ */
  noTenant?: boolean;
  signal?: AbortSignal;
  raw?: boolean;
}

/**
 * ต่ออายุ token อัตโนมัติเมื่อเจอ 401
 *
 * เก็บ promise ของการ refresh ไว้ตัวเดียว เพราะหน้า dashboard ยิงหลาย query
 * พร้อมกัน ถ้าปล่อยให้แต่ละอันเรียก /refresh เอง จะกลายเป็นการใช้ refresh token
 * ซ้ำ ซึ่งฝั่ง server ตีความว่า token ถูกขโมย (ข้อ 5.2) แล้วเตะออกจากระบบทุกอุปกรณ์
 */
let refreshing: Promise<boolean> | null = null;

async function refreshToken(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) return false;
      const data = await res.json();
      useAuth.getState().setSession(data);
      return true;
    } catch {
      return false;
    } finally {
      // ปล่อย lock ในรอบถัดไปของ event loop เพื่อให้ทุก caller ที่รออยู่ได้ผลเดียวกัน
      setTimeout(() => { refreshing = null; }, 0);
    }
  })();
  return refreshing;
}

async function raw(path: string, options: RequestOptions = {}, retried = false): Promise<Response> {
  const { accessToken, activeTenantId } = useAuth.getState();
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!options.anonymous && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (!options.anonymous && !options.noTenant && activeTenantId) headers['X-Tenant-Id'] = activeTenantId;

  const res = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (res.status === 401 && !options.anonymous && !retried) {
    if (await refreshToken()) return raw(path, options, true);
    useAuth.getState().clear();
  }
  return res;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await raw(path, options);

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }

  if (!res.ok) {
    const body = (parsed as { error?: ApiErrorBody })?.error ?? {
      code: 'INTERNAL_ERROR',
      message: typeof parsed === 'string' && parsed ? parsed : `เกิดข้อผิดพลาด (${res.status})`,
    };
    throw new ApiError(res.status, body);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, o?: RequestOptions) => request<T>(path, { ...o, method: 'GET' }),
  post: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>(path, { ...o, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>(path, { ...o, method: 'PATCH', body }),
  del: <T>(path: string, o?: RequestOptions) => request<T>(path, { ...o, method: 'DELETE' }),
  raw,
};

/** ข้อความ error ที่เอาไปแสดงให้ผู้ใช้ได้ทันที — API ส่งภาษาไทยมาอยู่แล้ว */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) {
    if (err.name === 'AbortError') return 'ยกเลิกการเชื่อมต่อ';
    if (err.message === 'Failed to fetch') return 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ต';
    return err.message;
  }
  return 'เกิดข้อผิดพลาดที่ไม่คาดคิด';
}
